import { Autocomplete, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { resetMarketplaceFilters, setSpecializationFilter, setLevelFilter, setSkillFilter, setWorkSiteFilter } from "../../../Redux/slices/TalentMarketplaceFiltersSlice";
import { useAppDispatch, useAppSelector } from "../../../Redux/store";
import { TalentSpecializations } from "../../../Types/General";
import { IsNullOrEmpty, toPascalCase } from "../../../Utils/String";
import { useMemo, useState } from "react";

interface IProps {}

export interface SearchFilters {
    Skill: string | undefined;
    Level: string | undefined;
    Specialization: string | undefined;
    WorkSite: string | undefined;
    Location: string | undefined;
}

//TODO: Resolve the type errors in this file and get rid of the ignores
export const TalentSearchFilters = ({}:IProps) => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const marketplaceFilters = useAppSelector( state => state.TalentMarketplaceFilters);
    const skillOptions = useMemo(() => {
        const specialization = marketplaceFilters.Specialization;
        let filters: string[] | undefined = []
        if (!IsNullOrEmpty(specialization)) {
            filters = TalentSpecializations.find( s => s.label === specialization)?.filters;
        } else {
            filters = TalentSpecializations.map( s => s.filters).flat();
        }
        return filters?.sort();
    }, [marketplaceFilters.Specialization])
    /* ******************** Variables ******************** */
    const filters = [
        {
            label: "Skill",
            minWidth: "80px",
            options: skillOptions ?? [],
            multiple: true
        },
        {
            label: "Level",
            minWidth: "100px",
            //TODO: Turn this into constants or enums
            options: [
                "Entry-level (0-2 years)",
                "Mid-level (3-5 years)",                
                "Senior-level (6+ years)",
            ]
        },
        {
            label: "Specialization",
            minWidth: "160px",
            options: TalentSpecializations.map( t => t.label)
        },
        {
            label: "Work site",
            minWidth: "115px",
            options: [
                "Remote",
                "In person",
                "Hybrid"
            ]
        },
        // {
        //     label: "Location",
        //     minWidth: "115px",
        //     options: africanCountries,
        // }
    ]
    /* ******************** Functions ******************** */
    const formatFilterLabel = (label: string) => (toPascalCase(label).replace(' ', ''))
    const getFilterChips = () => {
        const flattenedSkills = marketplaceFilters.Skill.map( s => ({ label: 'Skill', value: s}))
        //@ts-ignore
        const combinedFilters = flattenedSkills.concat([
            {
                label: 'Level',
                value: marketplaceFilters.Level,
            },
            {
                label: 'WorkSite',
                value: marketplaceFilters.WorkSite,
            },
            {
                label: 'Specialization',
                value: marketplaceFilters.Specialization,
            },
        ])
        //@ts-ignore
        return combinedFilters.filter( f => (f.value?.length ?? 0) > 0)
    }
    const handleReset = () =>{
        dispatch(resetMarketplaceFilters());
    }
    const handleFilterChange = (label: string, value: string) =>{
        switch(label) {
            case 'Skill':
                if (value) {
                    const newSkills = Array.from(marketplaceFilters.Skill);
                    //@ts-ignore
                    newSkills.push(value[value.length-1]);
                    dispatch(setSkillFilter(newSkills))
                }
                break;
            case 'Level':
                dispatch(setLevelFilter(value))
                break;
            case 'WorkSite':
                dispatch(setWorkSiteFilter(value))
                break;
            case 'Specialization':
                dispatch(setSpecializationFilter(value))
                break;
            default: 
                break;
        }

    }
    const handleChipDelete = (label: string, value: string) =>{
        switch(label) {
            case 'Skill':
                const newSkills = marketplaceFilters.Skill.filter( s => s !== value);
                dispatch(setSkillFilter(newSkills))
                break;
            case 'Level':
                dispatch(setLevelFilter(''))
                break;
            case 'WorkSite':
                dispatch(setWorkSiteFilter(''))
                break;
            case 'Specialization':
                dispatch(setSpecializationFilter(''))
                break;
            default: 
                break;
        }
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return (
        <>
            
            <Box
                display={"flex"}
                flexDirection={"column"}
                marginY={2}
                gap={2}
            >
                <Box
                    display={"flex"}
                    flexDirection={"row"}
                    width={'100%'}
                    gap={2}
                    justifyContent={'center'}
                    alignItems={"center"}
                    sx={{
                        " .MuiList-root":{
                            height: '300px'
                        },
                    }}
                >
                    {
                        filters.map((filter, idx) =>(
                            <Autocomplete 
                                freeSolo={filter.label === 'Skill'}
                                disableClearable
                                renderTags={() => null}
                                multiple={filter?.multiple ?? false}
                                options={filter.options}
                                value={
                                    //@ts-ignore
                                    filter.label === 'Skill' ? marketplaceFilters[formatFilterLabel(filter.label)]: ''
                                }                             
                                onChange={(params, value) => {
                                    const label = formatFilterLabel(filter.label);
                                    //@ts-ignore
                                    // const value = params.target.innerText;
                                    handleFilterChange(label, value);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={filter.label}
                                        size="small"
                                        value={''}
                                        sx={{
                                            color: 'primary.main',
                                            width: '210px',
                                            marginTop: 1,
                                            ".MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":{
                                                borderRadius: "12px",
                                                border: "2px solid"
                                            },
                                            ".MuiFormLabel-root":{
                                                color: 'primary.main',
                                            }
                                        }}
                                    />
                                )}
                            />
                        ))
                    }
                    <Button variant="text" sx={{marginTop: 1}} onClick={handleReset}>Reset</Button>
                </Box>
                <Stack direction='row' flexWrap={'wrap'} gap={1} maxHeight={'72px'} overflow={'auto'}>
                    {
                        getFilterChips().map( (c, idx) => (
                            <Chip 
                                key={`${c.label}-${idx}`}
                                label={c.value} 
                                onDelete={() => handleChipDelete(c.label, c.value)}
                            />
                        ))
                    }
                </Stack>
            </Box>
        </>
    )
}